import { useState, useRef, useEffect } from 'react';
import { Camera, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

interface ScannedItem {
  id: string;
  name: string;
  price: number;
  timestamp: Date;
  image?: string;
}

export default function PriceScanner() {
  const [items, setItems] = useState<ScannedItem[]>([]);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [itemName, setItemName] = useState('');
  const [itemPrice, setItemPrice] = useState('');
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showCamera, setShowCamera] = useState(false);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraStream(stream);
        setShowCamera(true);
      }
    } catch (err) {
      toast.error('Unable to access camera');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
      setShowCamera(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0);
        stopCamera();
        setShowPriceModal(true);
        toast.success('Photo captured! Add the price.');
      }
    }
  };

  const addItem = () => {
    if (!itemName.trim() || !itemPrice.trim()) {
      toast.error('Please enter item name and price');
      return;
    }

    const newItem: ScannedItem = {
      id: Date.now().toString(),
      name: itemName,
      price: parseFloat(itemPrice),
      timestamp: new Date(),
    };

    if (canvasRef.current) {
      newItem.image = canvasRef.current.toDataURL();
    }

    setItems([newItem, ...items]);
    setItemName('');
    setItemPrice('');
    setShowPriceModal(false);
    toast.success(`"${itemName}" added to your list!`);
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
    toast.success('Item removed');
  };

  const totalPrice = items.reduce((sum, item) => sum + item.price, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 p-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8 pt-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Price Scanner</h1>
        <p className="text-muted-foreground">Scan items and track their prices</p>
      </div>

      {/* Camera Section */}
      <div className="max-w-2xl mx-auto mb-8">
        {!showCamera ? (
          <Button
            onClick={startCamera}
            size="lg"
            className="w-full h-48 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex flex-col gap-3"
          >
            <Camera className="w-12 h-12" />
            Tap to Scan Item
          </Button>
        ) : (
          <div className="bg-black rounded-2xl overflow-hidden shadow-xl">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full aspect-video object-cover"
            />
            <div className="flex gap-3 p-4 bg-black">
              <Button onClick={stopCamera} variant="outline" className="flex-1">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={capturePhoto} className="flex-1 bg-primary hover:bg-primary/90">
                <Camera className="w-4 h-4 mr-2" />
                Capture
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Price Modal */}
      <Dialog open={showPriceModal} onOpenChange={setShowPriceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Price</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Item name (e.g., Coffee)"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              className="text-lg"
            />
            <Input
              placeholder="Price (e.g., 5.99)"
              type="number"
              step="0.01"
              value={itemPrice}
              onChange={(e) => setItemPrice(e.target.value)}
              className="text-lg"
            />
            <Button
              onClick={addItem}
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Items List */}
      <div className="max-w-2xl mx-auto">
        {items.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg mb-4">Start scanning to track prices</p>
            <p className="text-muted-foreground/60">Your scanned items will appear here</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-8">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="bg-card rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between border border-border"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">{item.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {item.timestamp.toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-2xl font-bold text-primary">${item.price.toFixed(2)}</p>
                    <Button
                      onClick={() => removeItem(item.id)}
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 border border-primary/20 sticky bottom-0">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-foreground">Total Price</span>
                <span className="text-3xl font-bold text-primary">${totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
